const FG_PRODUCT_PAGE_BUILD = '1.1.1';
const PRODUCT_PATH_PATTERN = /\/products\/[^/]+\/?$/;
const JUDGE_WIDGET_SELECTOR = '.jdgm-review-widget';
const JUDGE_WRITE_SELECTOR = '[data-testid="write-review-button"], .jdgm-write-rev-link';

class FGProductPage {
  constructor() {
    if (!PRODUCT_PATH_PATTERN.test(window.location.pathname)) return;

    document.documentElement.dataset.fgProductPageBuild = FG_PRODUCT_PAGE_BUILD;
    this.root = document.querySelector('.template-product .product-information');
    this.product = this.root?.querySelector(':scope > product-component[data-product-grid-content]');
    this.observer = null;

    if (!(this.root instanceof HTMLElement) || !(this.product instanceof HTMLElement)) return;

    this.mount();
    this.observer = new MutationObserver(() => this.mount());
    this.observer.observe(this.root, { childList: true, subtree: true });
  }

  mount() {
    this.mountEssentials();

    const widget = this.root.querySelector(JUDGE_WIDGET_SELECTOR);
    if (!(widget instanceof HTMLElement)) return;

    const expectedProductId = this.product.dataset.fgProductId || '';
    const widgetProductId = widget.dataset.id || widget.dataset.productId || '';
    if (expectedProductId && widgetProductId && expectedProductId !== widgetProductId) return;

    widget.dataset.shopReviews = 'false';

    let shell = this.root.querySelector('[data-fg-product-review-shell]');
    if (!(shell instanceof HTMLElement)) {
      shell = document.createElement('section');
      shell.className = 'fg-product-page__review-shell';
      shell.dataset.fgProductReviewShell = '';
      shell.setAttribute('aria-labelledby', 'fg-product-review-title');
      shell.innerHTML = `
        <header class="fg-product-page__review-header">
          <div>
            <span class="fg-product-page__eyebrow">${window.FG_PRODUCT_PAGE_I18N?.reviewEyebrow || window.fgText("FATHERGLORIFIED / COMMUNITY VOICES")}</span>
            <h2 id="fg-product-review-title">${window.FG_PRODUCT_PAGE_I18N?.reviewHeading || "WHAT OTHERS SAY ABOUT OUR PIECES."}</h2>
          </div>
          <div class="fg-product-page__review-actions">
            <button class="fg-product-page__action" type="button" data-fg-page-write-review disabled>
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
                <path d="m12 3 2.7 5.47 6.03.88-4.36 4.25 1.03 6-5.4-2.84-5.4 2.84 1.03-6-4.36-4.25 6.03-.88L12 3Z"/>
              </svg>
              <span>${window.fgText("WRITE A REVIEW")}</span>
            </button>
            <button class="fg-product-page__action fg-product-page__action--secondary" type="button" data-fg-page-copy-link>
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
                <path d="M9.5 14.5 14.5 9M7.05 16.95l-1 1a3.5 3.5 0 0 1-4.95-4.95l4-4a3.5 3.5 0 0 1 4.95 0M16.95 7.05l1-1a3.5 3.5 0 0 1 4.95 4.95l-4 4a3.5 3.5 0 0 1-4.95 0"/>
              </svg>
              <span>${window.fgText("COPY LINK")}</span>
            </button>
            <span class="fg-product-page__status" data-fg-page-review-status aria-live="polite"></span>
          </div>
        </header>
        <div class="fg-product-page__review-widget" data-fg-product-review-widget></div>
      `;

      this.product.after(shell);

      shell.querySelector('[data-fg-page-write-review]')?.addEventListener('click', () => {
        this.openReviewForm();
      });
      shell.querySelector('[data-fg-page-copy-link]')?.addEventListener('click', () => {
        this.copyLink();
      });
    }

    const story = this.root.querySelector('[data-fg-product-description]');
    const details = story?.querySelector('.fg-product-preview__details');

    if (details instanceof HTMLElement && details.parentElement !== this.product) {
      details.classList.add('fg-product-preview__details--standalone');
      this.product.append(details);
    }

    if (story instanceof HTMLElement) {
      if (story.nextElementSibling !== shell) story.after(shell);
      if (details instanceof HTMLElement && shell.nextElementSibling !== details) shell.after(details);
    } else if (shell.parentElement !== this.root || shell.previousElementSibling !== this.product) {
      this.product.after(shell);
    }

    const widgetSlot = shell.querySelector('[data-fg-product-review-widget]');
    const appBlock = this.findAppBlock(widget);
    if (widgetSlot instanceof HTMLElement && appBlock && appBlock.parentElement !== widgetSlot) {
      widgetSlot.append(appBlock);
    }

    const writeButton = shell.querySelector('[data-fg-page-write-review]');
    if (writeButton instanceof HTMLButtonElement) {
      writeButton.disabled = !(widget.querySelector(JUDGE_WRITE_SELECTOR) instanceof HTMLElement);
    }
  }

  mountEssentials() {
    if (this.product.querySelector(':scope > .fg-product-essentials')) return;

    const template = document.querySelector('template[data-fg-product-essentials-template]');
    const essentials = template?.content.querySelector('.fg-product-essentials')?.cloneNode(true);
    if (!(essentials instanceof HTMLElement)) return;

    const story = this.product.querySelector(':scope > [data-fg-product-description]');
    if (story instanceof HTMLElement) {
      story.before(essentials);
    } else {
      this.product.append(essentials);
    }
  }

  findAppBlock(widget) {
    const explicitBlock = widget.closest('.shopify-app-block, [id^="shopify-block-"]');
    if (explicitBlock instanceof HTMLElement) return explicitBlock;

    let candidate = widget;
    while (candidate.parentElement && candidate.parentElement !== this.root) {
      candidate = candidate.parentElement;
    }
    return candidate instanceof HTMLElement ? candidate : widget;
  }

  openReviewForm() {
    const shell = this.root.querySelector('[data-fg-product-review-shell]');
    const widget = shell?.querySelector(JUDGE_WIDGET_SELECTOR);
    const trigger = widget?.querySelector(JUDGE_WRITE_SELECTOR);

    if (trigger instanceof HTMLElement) {
      trigger.click();
      this.setStatus('');
      return;
    }

    this.setStatus(window.fgText("REVIEW FORM LOADING"));
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      const lateWidget = shell?.querySelector(JUDGE_WIDGET_SELECTOR);
      const lateTrigger = lateWidget?.querySelector(JUDGE_WRITE_SELECTOR);
      if (lateTrigger instanceof HTMLElement) {
        window.clearInterval(timer);
        lateTrigger.click();
        this.setStatus('');
      } else if (attempts >= 50) {
        window.clearInterval(timer);
        this.setStatus('REVIEW FORM UNAVAILABLE');
      }
    }, 100);
  }

  async copyLink() {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.search = '';
    cleanUrl.hash = '';

    let copied = false;
    try {
      await navigator.clipboard.writeText(cleanUrl.toString());
      copied = true;
    } catch {
      const field = document.createElement('textarea');
      field.value = cleanUrl.toString();
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.append(field);
      field.select();
      copied = document.execCommand('copy');
      field.remove();
    }

    this.setStatus(copied ? window.fgText("LINK COPIED") : window.fgText("COPY FAILED"));
  }

  setStatus(message) {
    const status = this.root.querySelector('[data-fg-page-review-status]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = message;
    if (message) window.setTimeout(() => {
      if (status.textContent === message) status.textContent = '';
    }, 2600);
  }
}

const initialize = () => new FGProductPage();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}
